import React, { forwardRef } from 'react';
import {
  Card, Col, Row, UncontrolledTooltip,
} from 'reactstrap';
import PropTypes from 'prop-types';
import Translate from '@securitize/ui-components/i18n/Translate/Translate';
import classNames from 'classnames';
import { Formik } from 'formik';
import * as Yup from 'yup';
import './Calculator.scss';
import Select from 'react-select';
import LaddaButton, { ZOOM_OUT } from 'react-ladda';
import CurrencyToSymbol from '../../../CurrencyToSymbol';
import AutoNumericInput from '../../../AutoNumericInput';

const Calculator = forwardRef(({
  initValue,
  initialCurrency,
  currencyList,
  onChange,
  isTokenCalculatorVisible,
  inputId,
  isLoading,
  onInvestClick,
  tokenName,
  tokenDecimals,
}, inputRef) => {
  const initCurrency = currencyList.find(({ name }) => name === initialCurrency);

  const isCrypto = (currency) => ['BTC', 'ETH', 'BCH'].includes((currency || '').toUpperCase());

  const calculateBonusPercent = (amount, tiers) => {
    const bonus = (tiers || [])
      .sort((a, b) => b.minInvestmentAmount - a.minInvestmentAmount)
      .find(({ minInvestmentAmount }) => minInvestmentAmount < amount);
    return bonus ? bonus.bonusPercentage / 100 : 0;
  };

  const validationSchema = Yup.object().shape({
    minAmount: Yup.number(),
    name: Yup.string(),
    investValue: Yup.number().min(Yup.ref('minAmount'))
      .typeError('Please enter a value')
      .required('Please enter a value'),
  });

  const minAmountDisplay = (currencyName, minAmount) => (
    <span>
      <Translate
        translationKey="Texts.minimum"
      />
      <span>
        <span>&nbsp;</span>
        <Translate
          translationKey="Texts.amount"
        />
        <span>&nbsp;</span>
        <CurrencyToSymbol currency={currencyName} />
        {Number(minAmount).toLocaleString(undefined, {
          minimumFractionDigits: (isCrypto(currencyName) ? 4 : 2),
          maximumFractionDigits: (isCrypto(currencyName) ? 4 : 2),
        })}
      </span>
    </span>
  );

  const validate = (values) => {
    try {
      validationSchema.validateSync(values, { abortEarly: false });
      onChange({ ...values, isValid: true });
      return {};
    } catch (error) {
      onChange({ ...values, isValid: false });
      return {
        investValue: minAmountDisplay(values.name, values.minAmount),
      };
    }
  };

  return (
    <Formik
      initialValues={{ investValue: initValue, ...(initCurrency || currencyList[0] || {}) }}
      validate={validate}
      validateOnMount
      enableReinitialize
      onSubmit={() => undefined}
    >
      {(formikBag) => {
        const convertedAmount = formikBag.values.investValue * formikBag.values.value || 0;
        const investValue = parseFloat(convertedAmount.toFixed(tokenDecimals));
        const bonusValue = parseFloat(
          (calculateBonusPercent(formikBag.values.investValue, formikBag.values.tiers) * investValue)
            .toFixed(tokenDecimals),
        );
        const investValueError = !formikBag.isValid && formikBag.touched.investValue && formikBag.errors.investValue;

        const decimalPlaces = isCrypto(formikBag.values.name) ? 4 : 2;

        return (
          <Card className="securitize-calculator p-3 p-xl-4 border-0 box-shadow">
            <Row className="securitize-calculator__title">
              <Col>
                <i className="icon icon-calculator mr-3" />
                <Translate translationKey="Texts.investment" />
              </Col>
            </Row>
            <Row className="securitize-calculator__amounts justify-content-between">
              <Col md={12} className="securitize-calculator__amounts__amount-block">
                <Card
                  className={classNames('securitize-calculator__amounts__amount-block__card p-4 m-0', {
                    'securitize-calculator__amounts__card--invalid': investValueError,
                  })}
                >
                  <div className="securitize-calculator__amounts__amount-block__card__investment">
                    <Select
                      arrowRenderer={({ isOpen }) => {
                        if (currencyList.length === 1) {
                          return <></>;
                        }
                        return (
                          <div className="securitize-calculator__amounts__amount-block__card__investment__arrow-icon">
                            <i className={!isOpen ? 'icon-point-down' : 'icon-point-up'} />
                          </div>
                        );
                      }}
                      required
                      className="securitize-calculator__amounts__amount-block__card__investment__currency-select font-28"
                      value={{ label: formikBag.values.name }}
                      onChange={({ value }) => {
                        formikBag.setFieldValue('minAmount', value.minAmount.toFixed(2));
                        formikBag.setFieldValue('value', value.value);
                        formikBag.setFieldValue('tiers', value.tiers);
                        formikBag.setFieldValue('oneShareValue', value.oneShareValue);
                        window.setTimeout(() => formikBag.setFieldValue('name', value.name));
                      }}
                      options={currencyList.map((item) => ({ label: item.name, value: item }))}
                      clearable={false}
                      searchable={false}
                      disabled={currencyList.length === 1}
                    />
                    <AutoNumericInput
                      id={inputId}
                      ref={inputRef}
                      className={classNames(
                        'securitize-calculator__amounts__amount-block__card__investment__amount-input font-28', {
                          'securitize-calculator__amounts__amount-block__card__investment__amount-input--error':
                            investValueError && formikBag.values.investValue > 0,
                          'securitize-calculator__amounts__amount-block__card__investment__amount-input--success':
                            !investValueError && formikBag.values.investValue > 0,
                        },
                      )}
                      decimalPlaces={decimalPlaces}
                      onChange={(value) => {
                        formikBag.setTouched({ investValue: true });
                        formikBag.setFieldValue('investValue', value);
                      }}
                      value={formikBag.values.investValue}
                    />
                  </div>
                  <div
                    className="securitize-calculator__amounts__amount-block__card__investment-rates"
                  >
                    <div
                      className="securitize-calculator__amounts__center-block__one-share font-14 issuer-primary"
                    >
                      {
                        investValueError
                          ? (
                            <span
                              className={classNames('p-3 text-uppercase d-block text-center text-truncate',
                                { 'text-danger': investValueError })}
                            >
                              {formikBag.errors.investValue}
                            </span>
                          )
                          : (
                            <span
                              className="p-3 text-uppercase d-block text-center text-nowrap overflow-hidden text-truncate"
                            >
                              {isTokenCalculatorVisible ? (
                                <>
                                  <span>1</span>
                                &nbsp;
                                  {tokenName}
                                  <span>
                                    {` = ${parseFloat(formikBag.values.oneShareValue)
                                      .toLocaleString(undefined, { maximumFractionDigits: 10 })} ${formikBag.values.name}`}
                                  </span>
                                </>
                              ) : (
                                minAmountDisplay(formikBag.values.name, formikBag.values.minAmount)
                              )}
                            </span>
                          )
                      }
                    </div>
                  </div>
                </Card>
              </Col>
              {isTokenCalculatorVisible && (
                <>
                  <Col
                    md={12}
                    className="securitize-calculator__amounts__conversion-block d-flex align-items-start justify-content-between"
                  >
                    <Card className="p-4 m-0 border-0 securitize-calculator__amounts__conversion-block__card">
                      <div className="d-flex justify-content-between align-content-center font-28">
                        <div className=" mr-2 text-uppercase align-self-end">
                          <div className="position-relative">
                            <p className="securitize-calculator__amounts__conversion-block__card__token-name">{tokenName}</p>
                            <i id="disclaimer-info" className="icon icon-info" />
                            <UncontrolledTooltip
                              style={{
                                fontSize: '12px',
                                fontWeight: '300',
                                marginLeft: '1em',
                                backgroundColor: '#FFF',
                                color: 'black',
                                boxShadow: '0 4px 20px 0 rgba(0, 0, 0, 0)',
                                maxWidth: '400px',
                                textAlign: 'left',
                                padding: '1em',
                              }}
                              hideArrow
                              placement="top-start"
                              target="disclaimer-info"
                            >
                              <Translate translationKey="Texts.calculator-disclaimer" />
                            </UncontrolledTooltip>
                          </div>
                        </div>
                        <span
                          id="tokens-received"
                          className="text-truncate"
                        >
                          {(investValue + bonusValue).toLocaleString(undefined, { maximumFractionDigits: tokenDecimals })}
                        </span>
                      </div>
                    </Card>
                  </Col>
                </>
              )}
              <Col md={12} className="securitize-calculator__invest-btn">
                <LaddaButton
                  id="btn-invest"
                  loading={isLoading}
                  disabled={!formikBag.isValid}
                  onClick={onInvestClick}
                  color="primary"
                  data-style={ZOOM_OUT}
                  className="btn issuer-button-primary btn-lg btn-block font-weight-bold"
                  type="button"
                >
                  <Translate
                    translationKey="Texts.invest"
                  />
                  &nbsp;
                  {formikBag.values.name}
                  &nbsp;
                  {parseFloat(formikBag.values.investValue).toLocaleString(undefined, { maximumFractionDigits: decimalPlaces })}
                </LaddaButton>
              </Col>
            </Row>
          </Card>
        );
      }}
    </Formik>
  );
});

Calculator.defaultProps = {
  initValue: 0,
  onChange: () => undefined,
  inputId: '',
  initialCurrency: 'USD',
  isTokenCalculatorVisible: true,
  onInvestClick: () => {},
  isLoading: false,
  tokenName: '',
  tokenDecimals: 0,
};

Calculator.propTypes = {
  initValue: PropTypes.number,
  inputId: PropTypes.string,
  initialCurrency: PropTypes.string,
  isTokenCalculatorVisible: PropTypes.bool,
  currencyList: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.number,
    minAmount: PropTypes.number,
  })).isRequired,
  onChange: PropTypes.func,
  onInvestClick: PropTypes.func,
  isLoading: PropTypes.bool,
  tokenName: PropTypes.string,
  tokenDecimals: PropTypes.number,
};

export default Calculator;
